<script setup lang="ts">
import { shallowRef, watch, onUnmounted, computed } from 'vue'
import { useLayersStore } from '@/stores/layers'
import { useMapsStore } from '@/stores/maps'

import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'

import { LayerItem } from '@/components/layers'
import { reorderList } from './utils'

const layersStore = useLayersStore()

const mapsStore = useMapsStore()

const props = defineProps<{
  layers: any[]
  // When set, this list represents a single Map: top-level reordering updates the
  // map's own layer order (maps/reorderMapLayers) instead of the global org order.
  mapId?: string
}>()

const items = shallowRef([...props.layers])

// Watch for external changes to layers prop (e.g., from the store)
watch(() => props.layers, (newLayers) => {
  items.value = [...newLayers]
})

// Flat position map: each layer and each child within a group gets a unique sequential z-position.
// Group header at position P, its children at P+1, P+2, … so LayerItem can compute child positions
// as (props.position + 1 + childIndex) without needing this map passed down as a prop.
const flatPositionMap = computed(() => {
  const map = new Map<string, number>()
  let pos = 0
  for (const layer of items.value) {
    map.set(layer.uuid, pos++)
    if (layer.properties?.data_type === 3) {
      for (const child of (layer.properties?.children || [])) {
        map.set(child.uuid, pos++)
      }
    }
  }
  return map
})

const emit = defineEmits(['deleteLayer'])

const deleteLayer = (layerId: string) => {
  emit('deleteLayer', layerId)
}

// After a cross-context move (parent change), reorder within the new context so the
// layer lands exactly where the user dropped it.
const reorderAfterGroupMove = (orgId: number, groupUuid: string, sourceId: string, targetId: string, dropPos: 'above' | 'below') => {
  const freshLayers = layersStore.getOrgLayers(orgId) as any[]
  const parent = freshLayers.find((l: any) => l.uuid === groupUuid)
  const children = parent?.properties?.children
  if (!children?.length) return
  const reordered = reorderList(children, sourceId, targetId, dropPos)
  layersStore.reorderChildren({ orgId, parentUuid: groupUuid, children: reordered })
}

const reorderAfterRootMove = (orgId: number, sourceId: string, targetId: string, dropPos: 'above' | 'below') => {
  const freshLayers = layersStore.getOrgLayers(orgId) as any[]
  const reordered = reorderList(freshLayers, sourceId, targetId, dropPos)
  layersStore.reorderOrgLayers({ orgId, layers: reordered })
}

// Monitor for drop events and reorder items
const cleanup = combine(
  monitorForElements({
    async onDrop({ location, source }) {
      if (!location.current.dropTargets.length) return

      const target = location.current.dropTargets[0]
      const sourceId = source.data.id as string
      const sourceParentUuid = source.data.parentUuid as string | undefined
      const targetId = target.data.id as string
      const targetParentUuid = target.data.parentUuid as string | undefined
      const dropPos = target.data.position as 'above' | 'below' | 'inside'

      if (sourceId === targetId) return

      // This monitor is global; with one LayerList mounted per Map, every list's
      // monitor fires on every drop. Only act on drags that started in THIS list,
      // and never consume a drop onto a Map header/section (cross-map move is
      // handled by MapsPanel's own monitor).
      if ((target.data as any).type === 'map') return
      const managedUuids = new Set<string>()
      for (const l of items.value) {
        managedUuids.add(l.uuid)
        for (const c of (l.properties?.children || [])) managedUuids.add(c.uuid)
      }
      if (!managedUuids.has(sourceId)) return
      // A drop whose target isn't in this list is landing in another map — let
      // MapsPanel's cross-map monitor own it; otherwise we'd re-persist the source
      // map's layers and duplicate the moved layer across both maps.
      if (!managedUuids.has(targetId)) return

      const orgId = items.value[0]?.properties?.organization_id

      // Drop onto a group header (inside zone) — layer lands at end, no positional reorder needed
      if (dropPos === 'inside') {
        const targetNumericId = target.data.numericId as number
        if (orgId && targetNumericId) {
          layersStore.moveLayerToGroup({ orgId, layerCode: sourceId, parentId: targetNumericId })
        }
        return
      }

      // Both children in the same group → reorder within the folder (local + DB)
      if (sourceParentUuid && targetParentUuid && sourceParentUuid === targetParentUuid) {
        const parent = items.value.find((l: any) => l.uuid === sourceParentUuid)
        if (parent && orgId) {
          const reordered = reorderList(parent.properties.children, sourceId, targetId, dropPos)
          items.value = items.value.map((l: any) =>
            l.uuid === sourceParentUuid
              ? { ...l, properties: { ...l.properties, children: reordered } }
              : l
          )
          layersStore.reorderChildren({ orgId, parentUuid: sourceParentUuid, children: reordered })
        }
        return
      }

      // Child dragged to a different group → move parent, then reorder at drop position
      if (sourceParentUuid && targetParentUuid && sourceParentUuid !== targetParentUuid) {
        const targetParent = items.value.find((l: any) => l.uuid === targetParentUuid)
        if (targetParent && orgId) {
          await layersStore.moveLayerToGroup({ orgId, layerCode: sourceId, parentId: (targetParent as any).id })
          reorderAfterGroupMove(orgId, targetParentUuid, sourceId, targetId, dropPos)
        }
        return
      }

      // Child dragged to root (dropped adjacent to a top-level layer) → remove from group, then reorder
      if (sourceParentUuid && !targetParentUuid) {
        if (orgId) {
          await layersStore.removeLayerFromGroup({ orgId, layerCode: sourceId })
          reorderAfterRootMove(orgId, sourceId, targetId, dropPos)
        }
        return
      }

      // Top-level dropped adjacent to a child → move into that child's parent group, then reorder
      if (!sourceParentUuid && targetParentUuid) {
        const targetParent = items.value.find((l: any) => l.uuid === targetParentUuid)
        if (targetParent && orgId) {
          await layersStore.moveLayerToGroup({ orgId, layerCode: sourceId, parentId: (targetParent as any).id })
          reorderAfterGroupMove(orgId, targetParentUuid, sourceId, targetId, dropPos)
        }
        return
      }

      // Both top-level → reorder the flat list (local + DB)
      const reorderedItems = reorderList(items.value, sourceId, targetId, dropPos)
      items.value = reorderedItems
      if (reorderedItems.length > 0) {
        const listOrgId = reorderedItems[0].properties?.organization_id
        if (listOrgId && props.mapId) {
          mapsStore.reorderMapLayers({
            orgId: listOrgId,
            mapId: props.mapId,
            layerCodes: reorderedItems.map((i: any) => i.uuid)
          })
        } else if (listOrgId) {
          layersStore.reorderOrgLayers({ orgId: listOrgId, layers: reorderedItems })
        }
      }
    },
  }),
)

// Clean up drag-and-drop listeners when component unmounts
onUnmounted(() => {
  cleanup()
})
</script>

<template>
  <div class="list-none select-none w-full bg-background text-foreground rounded-md text-sm font-medium">
    <LayerItem
      v-for="item in items"
      :key="item.uuid"
      :item="item"
      :position="flatPositionMap.get(item.uuid) ?? 0"
      @deleteLayer="deleteLayer"
    />
  </div>
</template>
